import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Button,
  Link,
  Chip,
  LinearProgress,
  FormGroup,
  Checkbox,
  Paper
} from '@mui/material';
import { 
  CloudUpload, 
  CheckCircle, 
  Warning, 
  Security, 
  VerifiedUser, 
  Policy,
  Assignment,
  DateRange,
  Language
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { subYears, isAfter } from 'date-fns';

const LegalInsuranceStep = ({ formData, updateFormData, errors }) => {
  const { user } = useAuth();
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const idFileInputRef = useRef(null);
  const coiFileInputRef = useRef(null);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData({ [field]: value });
  };

  const handleRadioChange = (field) => (event) => {
    const value = event.target.value === 'true';
    updateFormData({ [field]: value });
  };

  // Language options (moved from PersonalInfoStep)
  const languageOptions = [
    'English',
    'Spanish', 
    'French',
    'Portuguese',
    'Italian',
    'German',
    'Russian',
    'Chinese',
    'Japanese',
    'Arabic'
  ];

  // Date of birth validation (must be 18+ years old)
  const validateDateOfBirth = (date) => {
    if (!date) return false;
    const eighteenYearsAgo = subYears(new Date(), 18);
    return !isAfter(date, eighteenYearsAgo);
  };

  // Handle date change
  const handleDateChange = (date) => {
    updateFormData({ dateOfBirth: date });
  };

  // Handle language checkbox change
  const handleLanguageChange = (language) => (event) => {
    const isChecked = event.target.checked;
    const currentLanguages = formData.languages || [];
    
    if (isChecked) {
      updateFormData({ 
        languages: [...currentLanguages, language] 
      });
    } else {
      updateFormData({ 
        languages: currentLanguages.filter(lang => lang !== language) 
      });
    }
  };

  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }
    
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 5MB' };
    }
    
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'File must be JPEG, PNG, or PDF format' };
    }
    
    return { valid: true };
  };

  const handleFileUpload = (field) => () => {
    const fileInput = field === 'idUploadLink' ? idFileInputRef.current : coiFileInputRef.current;
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileSelect = (field) => (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadStatus(prev => ({
        ...prev,
        [field]: { error: validation.error, success: false }
      }));
      return;
    }

    // Clear previous status
    setUploadStatus(prev => ({
      ...prev,
      [field]: { error: null, success: false }
    }));

    // Start progress indicator
    setUploadProgress(prev => ({ ...prev, [field]: 10 }));

    // Real file upload to S3 via backend
    const uploadFile = async () => {
      try {
        setUploadProgress(prev => ({ ...prev, [field]: 30 }));
        
        // Create FormData for file upload
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        // Add user information and document type
        if (user?.id) {
          uploadFormData.append('userId', user.id);
        }
        if (user?.firstName && user?.lastName) {
          uploadFormData.append('userName', `${user.firstName}_${user.lastName}`);
        }
        
        // Determine document type based on field
        const documentType = field === 'idUploadLink' ? 'government_id' : 'insurance_certificate';
        uploadFormData.append('documentType', documentType);
        
        // Add tenant ID for multi-tenant architecture
        const tenantId = process.env.REACT_APP_TENANT_ID || 'primeshine';
        uploadFormData.append('tenantId', tenantId);
        
        setUploadProgress(prev => ({ ...prev, [field]: 60 }));
        
        // Upload file via backend endpoint
        const token = localStorage.getItem('token');
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/upload/file`, uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        
        setUploadProgress(prev => ({ ...prev, [field]: 90 }));
        
        // Update form data with real S3 URL
        updateFormData({ [field]: response.data.fileUrl });
        setUploadStatus(prev => ({
          ...prev,
          [field]: { error: null, success: true, fileName: file.name }
        }));
        setUploadProgress(prev => ({ ...prev, [field]: 0 }));
        
      } catch (error) {
        console.error('Upload error:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Upload failed. Please try again.';
        setUploadStatus(prev => ({
          ...prev,
          [field]: { 
            error: errorMessage, 
            success: false 
          }
        }));
        setUploadProgress(prev => ({ ...prev, [field]: 0 }));
      }
    };

    uploadFile();
  };

  const getUploadButtonText = (field) => {
    if (uploadProgress[field] > 0) {
      return 'Uploading...';
    }
    if (uploadStatus[field]?.success) {
      return 'Re-upload';
    }
    return field === 'idUploadLink' ? 'Upload ID Document' : 'Upload COI Document';
  };

  const getUploadStatus = (field) => {
    if (uploadProgress[field] > 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress[field]} 
            sx={{ width: 100 }}
          />
          <Typography variant="caption">{uploadProgress[field]}%</Typography>
        </Box>
      );
    }
    
    if (uploadStatus[field]?.error) {
      return (
        <Chip
          icon={<Warning />}
          label={uploadStatus[field].error}
          color="error"
          variant="outlined"
          size="small"
        />
      );
    }
    
    if (uploadStatus[field]?.success && uploadStatus[field]?.fileName) {
      return (
        <Chip
          icon={<CheckCircle />}
          label={`✓ ${uploadStatus[field].fileName}`}
          color="success"
          variant="outlined"
          size="small"
        />
      );
    }
    
    if (formData[field]) {
      return (
        <Chip
          icon={<CheckCircle />}
          label={`Uploaded: ${formData[field]}`}
          color="success"
          variant="outlined"
          size="small"
        />
      );
    }
    
    return null;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Legal & Insurance Information
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        We need to verify your work eligibility and insurance coverage to ensure compliance with legal requirements.
      </Typography>

      {/* Personal Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Language color="primary" /> Personal Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            {/* Date of Birth Field */}
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Birth *"
                  value={formData.dateOfBirth}
                  onChange={handleDateChange}
                  inputFormat="MM/dd/yyyy"
                  maxDate={new Date('2008-12-31')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      error={!!errors.dateOfBirth || (formData.dateOfBirth && !validateDateOfBirth(formData.dateOfBirth))}
                      helperText={errors.dateOfBirth || 
                        (formData.dateOfBirth && !validateDateOfBirth(formData.dateOfBirth) 
                          ? 'You must be at least 18 years old' 
                          : 'Required for age verification')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <DateRange sx={{ color: 'action.active', mr: 1 }} />
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* Languages Spoken Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Languages Spoken
                    </Typography>
                  </FormLabel>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Select all languages you can communicate in with clients. English is highly recommended.
                  </Typography>
                  <FormGroup>
                    <Grid container spacing={1}>
                      {languageOptions.map((language) => (
                        <Grid item xs={6} sm={4} md={3} key={language}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={(formData.languages || []).includes(language)}
                                onChange={handleLanguageChange(language)}
                                name={language}
                                color="primary"
                              />
                            }
                            label={language}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                  {formData.languages && formData.languages.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Please select at least one language for client communication.
                    </Alert>
                  )}
                </FormControl>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Residential Address Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🏠 Residential Address
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Complete Residential Address"
                placeholder="123 Main Street, Orlando, FL 32801"
                multiline
                rows={3}
                value={formData.residentialAddress || ''}
                onChange={handleChange('residentialAddress')}
                error={!!errors.residentialAddress}
                helperText={errors.residentialAddress || 'Your current home address (required for background check and tax purposes)'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Work Eligibility Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser color="primary" /> Work Eligibility & Background Check
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Chip 
                  icon={<Security />} 
                  label="Legal Verification Required" 
                  color="primary" 
                  variant="outlined" 
                  size="small" 
                />
              </Box>
              <FormControl error={!!errors.workAuthorized} required>
                <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                  Are you authorized to work in the United States?
                </FormLabel>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  We are required by law to verify work authorization for all employees
                </Typography>
                <RadioGroup
                  value={formData.workAuthorized === null || formData.workAuthorized === undefined ? '' : formData.workAuthorized.toString()}
                  onChange={handleRadioChange('workAuthorized')}
                  row
                >
                  <FormControlLabel 
                    value="true" 
                    control={<Radio />} 
                    label="Yes, I am authorized to work in the US" 
                  />
                  <FormControlLabel 
                    value="false" 
                    control={<Radio />} 
                    label="No, I am not authorized" 
                  />
                </RadioGroup>
                {errors.workAuthorized && (
                  <Typography color="error" variant="caption">
                    {errors.workAuthorized}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl error={!!errors.backgroundCheckConsent} required>
                <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                  Are you willing to submit to a background check?
                </FormLabel>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Background checks help ensure the safety of our clients and their homes
                </Typography>
                <RadioGroup
                  value={formData.backgroundCheckConsent === null || formData.backgroundCheckConsent === undefined ? '' : formData.backgroundCheckConsent.toString()}
                  onChange={handleRadioChange('backgroundCheckConsent')}
                  row
                >
                  <FormControlLabel 
                    value="true" 
                    control={<Radio />} 
                    label="Yes, I consent to a background check" 
                  />
                  <FormControlLabel 
                    value="false" 
                    control={<Radio />} 
                    label="No, I do not consent" 
                  />
                </RadioGroup>
                {errors.backgroundCheckConsent && (
                  <Typography color="error" variant="caption">
                    {errors.backgroundCheckConsent}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment color="primary" /> Government-Issued ID Upload
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Upload a clear, readable photo of your driver's license, state ID, or passport. 
                  Make sure all text is visible and the image is not blurry.
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={uploadProgress.idUploadLink > 0 ? null : <CloudUpload />}
                    onClick={handleFileUpload('idUploadLink')}
                    size="small"
                    disabled={uploadProgress.idUploadLink > 0}
                  >
                    {getUploadButtonText('idUploadLink')}
                  </Button>
                  
                  {getUploadStatus('idUploadLink')}
                  
                  {/* Hidden file input for ID document */}
                  <input
                    ref={idFileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect('idUploadLink')}
                  />
                </Box>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Insurance Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Policy color="primary" /> General Liability Insurance
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Required:</strong> General liability insurance protects both you and our clients in case of accidents. 
              Minimum coverage: $1,000,000. Don't have insurance? We can help you find affordable coverage starting at $15/month.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl error={!!errors.hasInsurance} required>
                <FormLabel>Do you have active general liability insurance?</FormLabel>
                <RadioGroup
                  value={formData.hasInsurance === null || formData.hasInsurance === undefined ? '' : formData.hasInsurance.toString()}
                  onChange={handleRadioChange('hasInsurance')}
                  row
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
                {errors.hasInsurance && (
                  <Typography color="error" variant="caption">
                    {errors.hasInsurance}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {formData.hasInsurance && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Insurance Provider Name"
                    placeholder="State Farm, Allstate, NEXT Insurance, etc."
                    value={formData.insuranceProvider || ''}
                    onChange={handleChange('insuranceProvider')}
                    error={!!errors.insuranceProvider}
                    helperText={errors.insuranceProvider || 'Name of your insurance company'}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Policy Number"
                    placeholder="ABC123456789"
                    value={formData.policyNumber || ''}
                    onChange={handleChange('policyNumber')}
                    error={!!errors.policyNumber}
                    helperText={errors.policyNumber || 'Policy number from your insurance documents'}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assignment color="primary" /> Certificate of Insurance (COI) Upload
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Upload your current Certificate of Insurance showing general liability coverage. 
                      The certificate should clearly show your name, policy number, and coverage amount.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={uploadProgress.coiUploadLink > 0 ? null : <CloudUpload />}
                        onClick={handleFileUpload('coiUploadLink')}
                        size="small"
                        disabled={uploadProgress.coiUploadLink > 0}
                      >
                        {getUploadButtonText('coiUploadLink')}
                      </Button>
                      
                      {getUploadStatus('coiUploadLink')}
                      
                      {/* Hidden file input for COI document */}
                      <input
                        ref={coiFileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect('coiUploadLink')}
                      />
                    </Box>
                  </Card>
                </Grid>
              </>
            )}

            {formData.hasInsurance === false && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Contact us if you need help getting your insurance</strong> 
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
        <Typography variant="body2" color="success.dark">
          🔒 <strong>Secure & Confidential:</strong> All documents are securely stored and encrypted. 
          We only use this information for verification and compliance purposes.
        </Typography>
      </Box>
    </Box>
  );
};

export default LegalInsuranceStep;